import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';

interface Creator {
  id: string;
  slug: string;
  name: string;
  role: string;
  country: string;
  avatar: string;
  verified: boolean;
  recent_work: string;
  specialties: string[];
  followers: number;
  fan_rating: number;
}

interface RelatedCreatorsProps {
  articleTags: string[];
  articleCategory: string;
  articleTitle: string;
}

const categoryToSpecialtyMap: Record<string, string[]> = {
  production: ['Drama', 'Independent Films', 'Visual Storytelling', 'Cinematography'],
  streaming: ['Drama', 'Comedy', 'Action', 'Romance'],
  awards: ['Drama', 'Historical', 'Social Drama', 'Documentary'],
  casting: ['Drama', 'Action', 'Comedy', 'Romance'],
  technology: ['Technical Innovation', 'Visual Storytelling', 'Cinematography'],
  industry: ['Independent Films', 'Documentary', 'Social Commentary'],
};

const formatFollowers = (count: number): string => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count.toString();
};

export default function RelatedCreators({
  articleTags,
  articleCategory,
  articleTitle,
}: RelatedCreatorsProps) {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCreators = async () => {
      try {
        const { data, error } = await supabase
          .from('creators')
          .select(
            'id, slug, name, role, country, avatar, verified, recent_work, specialties, followers, fan_rating',
          )
          .order('followers', { ascending: false })
          .limit(20);

        if (error) throw error;
        if (!data || data.length === 0) {
          setLoading(false);
          return;
        }

        const relevantSpecialties = categoryToSpecialtyMap[articleCategory] || [];
        const lowerTags = (articleTags || []).map((t) => t.toLowerCase());
        const lowerTitle = (articleTitle || '').toLowerCase();

        const scored = data.map((creator) => {
          let score = 0;
          const creatorSpecialties = (creator.specialties || []).map((s) =>
            s.toLowerCase(),
          );

          relevantSpecialties.forEach((rs) => {
            if (creatorSpecialties.includes(rs.toLowerCase())) score += 15;
          });

          const creatorNameLower = creator.name.toLowerCase();
          if (lowerTitle.includes(creatorNameLower)) score += 40;
          if (
            lowerTags.some(
              (t) => t.includes(creatorNameLower) || creatorNameLower.includes(t),
            )
          )
            score += 30;

          lowerTags.forEach((tag) => {
            if (
              creatorSpecialties.some(
                (s) => s.includes(tag) || tag.includes(s),
              )
            )
              score += 10;
          });

          const roleLower = creator.role.toLowerCase();
          if (lowerTags.includes(roleLower) || lowerTags.some((t) => t.includes(roleLower)))
            score += 12;

          if (creator.recent_work) {
            const recentLower = creator.recent_work.toLowerCase();
            if (lowerTitle.includes(recentLower)) score += 25;
            if (
              lowerTags.some(
                (t) => t.includes(recentLower) || recentLower.includes(t),
              )
            )
              score += 20;
          }

          if (creator.verified) score += 3;
          if (creator.followers > 1000000) score += 2;

          return { ...creator, _score: score };
        });

        const sorted = scored
          .sort((a, b) => b._score - a._score)
          .slice(0, 6);

        const hasRelevant = sorted.some((c) => c._score > 5);
        setCreators(
          hasRelevant
            ? sorted
            : scored
                .sort((a, b) => b.followers - a.followers)
                .slice(0, 6),
        );
      } catch (err) {
        console.error('Error fetching related creators:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCreators();
  }, [articleTags, articleCategory, articleTitle]);

  if (loading) {
    return (
      <div className="mt-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-32 h-5 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-3 py-2.5 bg-white rounded-lg border border-gray-200 animate-pulse flex-shrink-0"
            >
              <div className="w-9 h-9 rounded-full bg-gray-200" />
              <div className="space-y-1.5">
                <div className="w-20 h-3.5 bg-gray-200 rounded" />
                <div className="w-14 h-2.5 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (creators.length === 0) return null;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <i className="ri-user-star-line text-amber-500"></i>
          <h3 className="text-base font-bold text-gray-900">Related Creators</h3>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-500">{creators.length} found</span>
        </div>
        <Link
          to="/creators"
          className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 cursor-pointer"
        >
          View All
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      </div>

      <div className="flex flex-wrap gap-2.5">
        {creators.map((creator) => (
          <Link
            key={creator.id}
            to={`/creators/${creator.slug}`}
            className="group flex items-center gap-2.5 px-3 py-2.5 bg-white rounded-lg border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all"
          >
            <div className="relative flex-shrink-0">
              <img
                src={creator.avatar}
                alt={creator.name}
                className="w-9 h-9 rounded-full object-cover ring-1 ring-gray-100 group-hover:ring-amber-200 transition-all"
              />
              {creator.verified && (
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center ring-1 ring-white">
                  <i className="ri-check-line text-white" style={{ fontSize: '8px' }}></i>
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 text-xs group-hover:text-amber-600 transition-colors truncate max-w-[120px]">
                {creator.name}
              </h4>
              <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="truncate max-w-[60px]">{creator.role}</span>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <i className="ri-user-heart-line text-amber-400"></i>
                  {formatFollowers(creator.followers)}
                </span>
                <span className="flex items-center gap-0.5">
                  <i className="ri-star-fill text-amber-400"></i>
                  {creator.fan_rating}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}